import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Status labels come from the API already humanised (consultStatusLabel on the
// server maps the numeric DocLink codes). Grouping is done on those labels.
//
// There are three separate "finished" states in the DocLink enum — COMPLETED (8),
// APPT_COMPLETED (14) and CONFIRMED (19) — which is why the Completed and
// Confirmed tallies never matched on the old screen. They are shown as distinct
// columns here so the difference is visible rather than hidden in one number.
const STATUS_COLUMNS = [
  { key: "completed", label: "Completed",       match: ["Completed"] },
  { key: "apptEnded", label: "Appt Ended",      match: ["Appt Ended"] },
  { key: "confirmed", label: "HubMed Confirmed", match: ["HubMed Confirmed"] },
  { key: "cancelled", label: "Cancelled",       match: ["Cancelled"] },
  { key: "missed",    label: "Missed",          match: ["Missed Appt"] },
];

// Which columns count towards the invoice total. Kept as one constant so the
// billable definition is a single-line change once HubMed confirms what they
// actually pay on.
const BILLABLE_KEYS = ["completed"];

const LABEL_TO_KEY = STATUS_COLUMNS.reduce((acc, col) => {
  col.match.forEach((label) => { acc[label] = col.key; });
  return acc;
}, {});

const emptyCounts = () =>
  STATUS_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: 0 }), { other: 0, total: 0 });

/**
 * Group appointment rows by pharmacy and tally each status bucket.
 * Anything whose status isn't one of the five known buckets lands in `other`,
 * so the per-partner total always reconciles with the row count.
 */
function summarise(rows) {
  const byPharmacy = new Map();

  for (const row of rows) {
    const name = row.pharmacy?.trim() || "— Unattributed —";
    if (!byPharmacy.has(name)) byPharmacy.set(name, emptyCounts());
    const counts = byPharmacy.get(name);

    const key = LABEL_TO_KEY[row.status] || "other";
    counts[key] += 1;
    counts.total += 1;
  }

  const partners = [...byPharmacy.entries()]
    .map(([pharmacy, counts]) => ({ pharmacy, ...counts }))
    .sort((a, b) => a.pharmacy.localeCompare(b.pharmacy));

  const totals = partners.reduce((acc, p) => {
    STATUS_COLUMNS.forEach((c) => { acc[c.key] += p[c.key]; });
    acc.other += p.other;
    acc.total += p.total;
    return acc;
  }, emptyCounts());

  const billable = BILLABLE_KEYS.reduce((sum, k) => sum + totals[k], 0);

  return { partners, totals, billable };
}

const HubMedMonthlySummary = ({ month, year }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState(null);
  const [shownFor, setShownFor] = useState(null);

  const summary = useMemo(() => (rows ? summarise(rows) : null), [rows]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_APPOINTMENT_REPORT,
        "GET",
        null,
        { month, year }
      );
      if (!data?.rows?.length) {
        setRows(null);
        setError("No appointments found for the selected month.");
        return;
      }
      setRows(data.rows);
      setShownFor({ month, year });
    } catch (err) {
      setRows(null);
      setError(err?.message ?? "Failed to build the summary. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const periodLabel = shownFor
    ? `${MONTHS[shownFor.month - 1]} ${shownFor.year}`
    : "";

  const showsBillable = BILLABLE_KEYS.length > 0;

  return (
    <Box sx={{ mt: 5 }}>
      {/* Print rules: hide the app shell so only the report reaches the page.
          `visibility` rather than `display` keeps the table's layout intact. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .hm-printable, .hm-printable * { visibility: visible !important; }
          .hm-printable {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            padding: 0 !important; margin: 0 !important;
            box-shadow: none !important; border: none !important;
          }
          .hm-no-print { display: none !important; }
          .hm-printable table { page-break-inside: auto; }
          .hm-printable tr { page-break-inside: avoid; page-break-after: auto; }
          .hm-printable thead { display: table-header-group; }
        }
      `}</style>

      <Typography variant="h6" fontWeight="bold" mb={1} className="hm-no-print">
        Monthly Summary
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2} className="hm-no-print">
        Appointment counts per partner for the selected month, for invoicing
        HubMed. Use Save as PDF and choose &ldquo;Save as PDF&rdquo; as the
        destination.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }} className="hm-no-print">
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={18} /> : <SummarizeOutlinedIcon />}
          onClick={handleGenerate}
          disabled={loading}
        >
          {loading ? "Building…" : "Build monthly summary"}
        </Button>
        {summary && (
          <Button
            variant="contained"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => window.print()}
          >
            Save as PDF
          </Button>
        )}
      </Box>

      {error && (
        <Alert
          severity={error.startsWith("No appointments") ? "info" : "error"}
          className="hm-no-print"
        >
          {error}
        </Alert>
      )}

      {summary && (
        <Paper variant="outlined" className="hm-printable" sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            HubMed Appointments — {periodLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {summary.partners.length} partner
            {summary.partners.length === 1 ? "" : "s"} ·{" "}
            {summary.totals.total} appointment
            {summary.totals.total === 1 ? "" : "s"}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Partner</TableCell>
                {STATUS_COLUMNS.map((c) => (
                  <TableCell key={c.key} align="right" sx={{ fontWeight: 700 }}>
                    {c.label}
                  </TableCell>
                ))}
                {summary.totals.other > 0 && (
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Other</TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.partners.map((p) => (
                <TableRow key={p.pharmacy}>
                  <TableCell>{p.pharmacy}</TableCell>
                  {STATUS_COLUMNS.map((c) => (
                    <TableCell key={c.key} align="right">{p[c.key]}</TableCell>
                  ))}
                  {summary.totals.other > 0 && (
                    <TableCell align="right">{p.other}</TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{p.total}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { borderTop: "2px solid rgba(0,0,0,0.4)" } }}>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                {STATUS_COLUMNS.map((c) => (
                  <TableCell key={c.key} align="right" sx={{ fontWeight: 700 }}>
                    {summary.totals[c.key]}
                  </TableCell>
                ))}
                {summary.totals.other > 0 && (
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {summary.totals.other}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {summary.totals.total}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {showsBillable && (
            <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid rgba(0,0,0,0.12)" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Billable appointments: {summary.billable}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Counted from:{" "}
                {BILLABLE_KEYS
                  .map((k) => STATUS_COLUMNS.find((c) => c.key === k)?.label)
                  .join(", ")}
                . Confirm with HubMed which states they pay on before invoicing.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default HubMedMonthlySummary;
